import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import CatLoader from '../components/CatLoader';
import HypothesisTooltip, { useHypothesisData } from '../components/HypothesisTooltip';
import AdvChart from '../components/AdvChart';
import PositionsPanel from '../components/PositionsPanel';
import ClustersPanel from '../components/ClustersPanel';
import MassPositions from '../components/MassPositions';

const fmt = (v, dec = 0) => v == null || v === '' || isNaN(v) ? '—' : Number(v).toLocaleString('ru-RU', { maximumFractionDigits: dec });
const pct = (v) => v == null || isNaN(v) ? '—' : Number(v).toFixed(1) + '%';

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const todayMSK = () => {
  const now = new Date();
  return new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Moscow' }));
};
const todayStr = () => toISO(todayMSK());
const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 2, currentYear - 1, currentYear];

const iSt = {
  padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.04)', color: 'var(--text)', fontSize: 12,
};
const btnStyle = (active) => ({
  padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
  border: active ? '1px solid rgba(59,130,246,0.5)' : '1px solid rgba(255,255,255,0.08)',
  background: active ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.04)',
  color: active ? '#60a5fa' : 'rgba(200,215,235,0.7)',
  transition: 'all 0.15s',
  boxShadow: active ? '0 0 12px rgba(59,130,246,0.15)' : 'none',
});

const fmtDate = (s) => {
  const d = new Date(s + 'T00:00:00');
  return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}`;
};

function dateRange(from, to) {
  const dates = [];
  let cur = from;
  let safety = 0;
  while (cur <= to && safety < 400) {
    dates.push(cur);
    safety++;
    const parts = cur.split('-');
    const d = new Date(Number(parts[0]), Number(parts[1])-1, Number(parts[2])+1);
    cur = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  }
  return dates;
}

const modeLabels = { '7days': '7 дней', '14days': '14 дней', '30days': '30 дней', 'decade': 'Декада', 'year': 'Год', 'custom': 'Произвольно' };

function PeriodSelector({ onChange, initialFrom, initialTo, initialMode }) {
  const _savedPeriodMode = initialMode || null;
  const [mode, setMode] = useState(initialMode || '7days');
  const [year, setYear] = useState(currentYear);
  const [showYears, setShowYears] = useState(false);
  const [showPeriod, setShowPeriod] = useState(false);
  const [cFrom, setCFrom] = useState(initialFrom || toISO(new Date(todayMSK().getTime() - 7*86400000)));
  const [cTo, setCTo] = useState(initialTo || todayStr());

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current && initialFrom) { isFirstRender.current = false; return; }
    isFirstRender.current = false;
    const today = todayMSK();
    let f = null, t = toISO(today);
    if (mode === '7days')       { f = toISO(new Date(today.getTime() - 7*86400000)); }
    else if (mode === '14days') { f = toISO(new Date(today.getTime() - 14*86400000)); }
    else if (mode === '30days') { f = toISO(new Date(today.getTime() - 30*86400000)); }
    else if (mode === 'year')   { f = `${year}-01-01`; t = `${year}-12-31`; }
    else if (mode === 'decade') {
      const day = today.getDate();
      let ds, de;
      if (day <= 10) { ds = 1; de = 10; }
      else if (day <= 20) { ds = 11; de = 20; }
      else { ds = 21; de = new Date(today.getFullYear(), today.getMonth()+1, 0).getDate(); }
      f = toISO(new Date(today.getFullYear(), today.getMonth(), ds));
      t = toISO(new Date(today.getFullYear(), today.getMonth(), de));
    }
    if (f) onChange(f, t, mode);
  }, [mode, year]); // eslint-disable-line

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative' }}>
        <button onClick={() => { setShowYears(s => !s); setShowPeriod(false); }} style={btnStyle(year !== currentYear || showYears)}>
          {year} ▾
        </button>
        {showYears && (
          <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 100, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 6, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 90, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
            {YEARS.map(y => (
              <button key={y} onClick={() => {
                setYear(y); setShowYears(false);
                if (y !== currentYear) { setMode('custom'); const f=`${y}-01-01`, t=`${y}-12-31`; setCFrom(f); setCTo(t); onChange(f, t); localStorage.setItem('adv_period', JSON.stringify({ from: f, to: t, mode: 'custom' })); }
                else { setMode('30days'); }
              }} style={{ ...btnStyle(year === y), padding: '4px 12px', fontSize: 12 }}>{y}</button>
            ))}
          </div>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <button onClick={() => { setShowPeriod(s => !s); setShowYears(false); }} style={btnStyle(true)}>
          {modeLabels[mode] || mode} ▾
        </button>
        {showPeriod && (
          <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 100, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 6, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 130, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
            {(year === currentYear
              ? [{ k: '7days', l: '7 дней' }, { k: '14days', l: '14 дней' }, { k: '30days', l: '30 дней' }, { k: 'year', l: 'Год' }, { k: 'decade', l: 'Декада' }, { k: 'custom', l: 'Произвольно' }]
              : [{ k: 'custom', l: 'Весь год' }]
            ).map(b => (
              <button key={b.k} onClick={() => { setMode(b.k); setShowPeriod(false); }} style={{ ...btnStyle(mode === b.k), padding: '4px 12px', fontSize: 12, textAlign: 'left' }}>{b.l}</button>
            ))}
          </div>
        )}
      </div>

      {mode === 'custom' && (<>
        <input type="date" value={cFrom} onChange={e => setCFrom(e.target.value)} style={iSt} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
        <input type="date" value={cTo} onChange={e => setCTo(e.target.value)} style={iSt} />
        <button onClick={() => {
          onChange(cFrom, cTo, 'custom');
          localStorage.setItem('adv_period', JSON.stringify({ from: cFrom, to: cTo, mode: 'custom' }));
        }} style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8, background: 'linear-gradient(135deg,#2563eb,#3b82f6)', color: '#fff', border: 'none', cursor: 'pointer' }}>Применить</button>
      </>)}
    </div>
  );
}

const ROWS = [
  { key: 'orders_qty',      label: 'Заказы всего, шт',              group: 'Заказы' },
  { key: 'orders_sum',      label: 'Заказы всего, руб',              group: 'Заказы' },
  { key: 'price',           label: 'Наша цена',                      group: 'Заказы' },
  { key: 'spp_pct',         label: 'СПП+кошелёк, %',                group: 'Заказы', fmt: pct },
  { key: 'client_price',    label: 'Цена клиента',                   group: 'Заказы' },
  { key: 'organic_qty',     label: 'Органические заказы, шт',        group: 'Заказы' },
  { key: 'adv_orders_qty',  label: 'Рекламные заказы, шт',           group: 'Заказы' },
  { key: 'adv_orders_search', label: 'Рекл. заказы: поиск, шт', group: 'Заказы' },
  { key: 'adv_orders_shelf', label: 'Рекл. заказы: полки, шт', group: 'Заказы' },
  { key: 'assoc_orders', label: 'Ассоциированные заказы, шт', group: 'Заказы' },
  { key: 'organic_sum',     label: 'Органические заказы, руб',       group: 'Заказы' },
  { key: 'adv_order_sum',   label: 'Рекламные заказы, руб',          group: 'Заказы' },
  { key: 'adv_sum_search', label: 'Рекл. заказы: поиск, руб', group: 'Заказы' },
  { key: 'adv_sum_shelf', label: 'Рекл. заказы: полки, руб', group: 'Заказы' },
  { key: 'assoc_sum', label: 'Ассоциированные заказы, руб', group: 'Заказы' },
  { key: '_margin_forecast', label: 'Прогноз маржи',                  group: 'Заказы', fmt: (v) => v != null ? Number(v).toFixed(2) + '%' : '—', computed: (d) => { const m=d.margin_no_rk; const drr=d.drr_buyout; if(m==null||drr==null||!isFinite(m-drr))return null; return Math.round((m-drr)*100)/100; } },
  { key: 'spend_all',       label: 'Расходы РК всего',               group: 'Расходы РК' },
  { key: 'spend_search', label: 'Расходы РК: поиск', group: 'Расходы РК' },
  { key: 'spend_shelf', label: 'Расходы РК: полки', group: 'Расходы РК' },
  { key: 'drr_orders',      label: 'ДРР от суммы заказов',           group: 'ДРР', fmt: pct },
  { key: 'drr_buyout',      label: 'ДРР выкупа прогнозный',          group: 'ДРР', fmt: pct },
  { key: 'drr_search', label: 'ДРР поиск (от суммы заказов)', group: 'ДРР', fmt: pct },
  { key: 'drr_shelf', label: 'ДРР полки (от суммы заказов)', group: 'ДРР', fmt: pct },
  { key: 'views_all',       label: 'Показы всего',                   group: 'Показы' },
  { key: 'views_search', label: 'Показы: поиск', group: 'Показы' },
  { key: 'views_shelf', label: 'Показы: полки', group: 'Показы' },
  { key: 'clicks_all',      label: 'Клики всего',                    group: 'Клики' },
  { key: 'clicks_search', label: 'Клики: поиск', group: 'Клики' },
  { key: 'clicks_shelf', label: 'Клики: полки', group: 'Клики' },
  { key: 'cpm_all',         label: 'CPM всего',                      group: 'CPM / CPC / CTR', fmt: (v) => fmt(v, 2) },
  { key: 'cpm_search', label: 'CPM: поиск', group: 'CPM / CPC / CTR', fmt: (v) => fmt(v, 2) },
  { key: 'cpm_shelf', label: 'CPM: полки', group: 'CPM / CPC / CTR', fmt: (v) => fmt(v, 2) },
  { key: 'cpc_all',         label: 'CPC всего',                      group: 'CPM / CPC / CTR', fmt: (v) => fmt(v, 2) },
  { key: 'cpc_search', label: 'CPC: поиск', group: 'CPM / CPC / CTR', fmt: (v) => fmt(v, 2) },
  { key: 'cpc_shelf', label: 'CPC: полки', group: 'CPM / CPC / CTR', fmt: (v) => fmt(v, 2) },
  { key: 'ctr_all',         label: 'CTR всего',                      group: 'CPM / CPC / CTR', fmt: pct },
  { key: 'ctr_search', label: 'CTR: поиск', group: 'CPM / CPC / CTR', fmt: pct },
  { key: 'ctr_shelf', label: 'CTR: полки', group: 'CPM / CPC / CTR', fmt: pct },
  { key: 'atbs_all', label: 'Корзины всего (реклама)', group: 'Корзины (реклама)' },
  { key: 'atbs_search', label: 'Корзины: поиск', group: 'Корзины (реклама)' },
  { key: 'atbs_shelf', label: 'Корзины: полки', group: 'Корзины (реклама)' },
  { key: 'assoc_atbs', label: 'Ассоциированные корзины', group: 'Корзины (реклама)' },
  { key: 'conv_cart',       label: 'CR в корзину',                   group: 'Воронка', fmt: pct },
  { key: 'conv_order',      label: 'CR в заказ',                     group: 'Воронка', fmt: pct },
  { key: 'open_count',      label: 'Открытия карточки',              group: 'Воронка' },
  { key: 'cart_count',      label: 'Добавлений в корзину',           group: 'Воронка' },
];

function ArticleCard({ article, dates, tag='', onHide=()=>{}, onTag=()=>{}, hypoData={} }) {
  const nmId = article.nm_id || article.nmId;
  const [showChart, setShowChart] = useState(false);
  const [showPos, setShowPos] = useState(false);
  const [showCl, setShowCl] = useState(false);
  const [posKey, setPosKey] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);

  const today = todayStr();
  const dayData = dates.map(d => ({ date: String(d).slice(0, 10), ...(article.days[String(d).slice(0, 10)] || {}) }));
  const _lastDateStr = dates.length > 0 ? String(dates[dates.length-1]).slice(0,10) : null;
  const _isToday = _lastDateStr === todayStr();
  const mainDays = _isToday ? dayData.slice(0, -1) : dayData;
  const totalSpend = mainDays.reduce((s, d) => s + (Number(d.spend_all) || 0), 0);
  const totalOrders = mainDays.reduce((s, d) => s + (Number(d.orders_qty) || 0), 0);

  const totalAdvOrders = mainDays.reduce((s, d) => s + (Number(d.adv_orders_qty) || 0), 0);
  const drrDays = mainDays.filter(d => d.drr_orders != null);
  const avgDrr = drrDays.length > 0 ? drrDays.reduce((s, d) => s + Number(d.drr_orders), 0) / drrDays.length : 0;
  const drrColor = avgDrr > 30 ? '#ef4444' : avgDrr > 15 ? '#f59e0b' : '#10b981';
  const groups = [...new Set(ROWS.map(r => r.group))];
  const visibleRows = ROWS.filter(r => activeGroup === null || r.group === activeGroup);

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 12, borderLeft: `3px solid ${drrColor}`,
    }}>
      <div onClick={() => setExpanded(p => !p)} style={{
        padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16,
        background: expanded ? 'rgba(255,255,255,0.03)' : 'transparent',
      }}>
        <div style={{ width: 44, height: 44, borderRadius: 8, flexShrink: 0, background: 'var(--border)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          {article.photo
            ? <img src={article.photo} alt="" className="wb-photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>нет</span>}
          <button onClick={e => { e.stopPropagation(); onTag(article.nmId); }} style={{
            position: 'absolute', top: 1, left: 1, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, padding: 1, lineHeight: 1,
          }}>
            <span style={{
              borderRadius: 3, padding: '1px 3px', fontSize: 9,
              background: tag==='top'?'#10b981':tag==='flop'?'#ef4444':'rgba(0,0,0,0.5)',
              border: 'none',
              color: '#fff',
              fontWeight: 700,
              borderRadius: 3,
              padding: '2px 4px',
            }}>{tag==='top'?'▲':tag==='flop'?'▼':'○'}</span>
          </button>
          <button onClick={e => { e.stopPropagation(); onHide(article.nmId); }} style={{
            position: 'absolute', bottom: 1, right: 1, background: 'rgba(239,68,68,0.7)', border: 'none', cursor: 'pointer', fontSize: 11, padding: '1px 3px', lineHeight: 1, color: '#fff', fontWeight: 700, borderRadius: 3,
          }}>×</button>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{article.vendorCode}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}><span className="blur-nmid">{article.nmId}</span></div>
        </div>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexShrink: 0 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Расходы РК</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{fmt(totalSpend)} ₽</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Заказы</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{fmt(totalOrders)} шт</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Рекл. заказы</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{fmt(totalAdvOrders)} шт</div>
          </div>
          <div style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${drrColor}20`, color: drrColor, border: `1px solid ${drrColor}40` }}>
            ДРР {pct(avgDrr)}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 16 }}>{expanded ? '▲' : '▼'}</div>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <div style={{ padding: '10px 20px', display: 'flex', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
            <button onClick={() => setActiveGroup(null)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: activeGroup === null ? 'var(--accent)' : 'transparent', color: activeGroup === null ? '#fff' : 'var(--text-muted)' }}>Все</button>
            {groups.map(g => (
              <button key={g} onClick={() => setActiveGroup(ag => ag === g ? null : g)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: '1px solid var(--border)', background: activeGroup === g ? 'var(--accent)' : 'transparent', color: activeGroup === g ? '#fff' : 'var(--text-muted)' }}>{g}</button>
            ))}
          </div>
          <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
            <div style={{ minWidth: 'max-content' }}>
              {/* Шапка */}
              <div style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 10, background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 230, flexShrink: 0, padding: '8px 20px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 11, boxShadow: '2px 0 4px rgba(0,0,0,0.2)' }}>Показатель</div>
                {dates.map(d => (
                  <div key={d} style={{ width: 72, flexShrink: 0, padding: '8px 12px', textAlign: 'right', fontSize: 12, fontWeight: 500, color: d === dates[dates.length-1] && dates[dates.length-1] === todayStr() ? 'rgba(150,165,180,0.5)' : 'var(--text-muted)', whiteSpace: 'nowrap', background: d === dates[dates.length-1] && dates[dates.length-1] === todayStr() ? 'rgba(255,255,255,0.05)' : 'transparent' }}>{fmtDate(d)}{d === dates[dates.length-1] && dates[dates.length-1] === todayStr() ? ' ●' : ''}</div>
                ))}
              </div>
              {/* Строки */}
              {visibleRows.map((row, idx) => {
                const prevGroup = idx > 0 ? visibleRows[idx - 1].group : null;
                const isNewGroup = row.group !== prevGroup;
                const fmtFn = row.fmt || fmt;
                const isDrr = row.key === 'drr_orders' || row.key === 'drr_buyout' || row.key === 'drr_search' || row.key === 'drr_shelf';
                const bg = idx % 2 === 0 ? 'var(--surface)' : 'rgba(17,24,39,0.98)';
                return (
                  <>
                    {isNewGroup && (
                      <div key={`g-${row.group}`} style={{ display: 'flex', background: 'rgba(59,130,246,0.05)' }}>
                        <div style={{ width: 230, flexShrink: 0, padding: '8px 20px 4px', fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', left: 0, background: 'rgba(59,130,246,0.05)' }}>{row.group}</div>
                        {dates.map(d => <div key={d} style={{ width: 72, flexShrink: 0, background: 'rgba(59,130,246,0.05)' }} />)}
                      </div>
                    )}
                    <div key={row.key} style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.04)', background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                      <div style={{ width: 230, flexShrink: 0, padding: '7px 20px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: bg, boxShadow: '2px 0 4px rgba(0,0,0,0.2)', zIndex: 1 }}>{row.label}</div>
                      {(() => {
                        const mainVals = dayData.slice(0, _isToday ? -1 : undefined).map(d => Number(d[row.key]) || 0).filter(v => v > 0);
                        const minV = mainVals.length > 1 ? Math.min(...mainVals) : 0;
                        const maxV = mainVals.length > 1 ? Math.max(...mainVals) : 0;
                        return dayData.map((d, di) => {
                          const val = row.computed ? row.computed(d, article) : d[row.key];
                          const isLast = di === dayData.length - 1 && _isToday;
                          const numVal = Number(val) || 0;
                          let bg = 'transparent';
                          if (!isLast && mainVals.length > 1 && maxV > minV && numVal > 0) {
                            const ratio = (numVal - minV) / (maxV - minV);
                            const isCpc = row.key.startsWith('cpc_');
                            const isCpo = row.key.startsWith('cpo_');
                            const gradRatio = (isDrr || isCpc || isCpo) ? 1 - ratio : ratio;
                            const r = gradRatio < 0.5 ? 239 : Math.round(239 + (16-239)*(gradRatio-0.5)/0.5);
                            const g = gradRatio < 0.5 ? Math.round(68 + (158-68)*gradRatio/0.5) : Math.round(158 + (185-158)*(gradRatio-0.5)/0.5);
                            const b2 = gradRatio < 0.5 ? Math.round(68 + (11-68)*gradRatio/0.5) : Math.round(11 + (129-11)*(gradRatio-0.5)/0.5);
                            bg = `${r},${g},${b2}`;
                          }
                          const color = isLast ? 'rgba(150,165,180,0.4)' : bg !== 'transparent' ? bg.replace('rgba', 'rgb').replace(/,[\d.]+\)/, ')').replace('rgb', 'rgba').replace(')', ',1)') : 'var(--text)';
                          const textColor = isLast ? 'rgba(150,165,180,0.4)' : bg !== 'transparent' ? `rgb(${bg})` : 'var(--text)';
                          return (
                            <div key={d.date} style={{ width: 72, flexShrink: 0, padding: '7px 12px', textAlign: 'right', fontSize: 12, color: textColor, fontWeight: bg !== 'transparent' && !isLast ? 600 : 400, background: isLast ? 'rgba(255,255,255,0.05)' : 'transparent', display:'flex', alignItems:'center', justifyContent:'flex-end', gap:3, position:'relative' }}>
                              {row.key === 'orders_qty' && <HypothesisTooltip nmId={nmId} date={d.date} hypoData={hypoData} filterTypes={['card_update','stock','manual']}/>}
                              {fmtFn(val)}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {/* Кнопка графика */}
      <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', padding:'6px 12px' }}>
        <button onClick={()=>setShowChart(s=>!s)} style={{
          padding:'3px 12px', fontSize:11, borderRadius:6, cursor:'pointer',
          background: showChart?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.04)',
          border: showChart?'1px solid rgba(59,130,246,0.4)':'1px solid rgba(255,255,255,0.08)',
          color: showChart?'#60a5fa':'rgba(200,215,235,0.5)',
        }}>{showChart?'▲ Скрыть график':'📈 График'}</button>
        <button onClick={()=>setShowPos(s=>!s)} style={{
          marginLeft:8, padding:'3px 12px', fontSize:11, borderRadius:6, cursor:'pointer',
          background: showPos?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.04)',
          border: showPos?'1px solid rgba(59,130,246,0.4)':'1px solid rgba(255,255,255,0.08)',
          color: showPos?'#60a5fa':'rgba(200,215,235,0.5)',
        }}>{showPos?'▲ Скрыть позиции':'🔎 Позиции'}</button>
        <button onClick={()=>setShowCl(s=>!s)} style={{
          marginLeft:8, padding:'3px 12px', fontSize:11, borderRadius:6, cursor:'pointer',
          background: showCl?'rgba(59,130,246,0.2)':'rgba(255,255,255,0.04)',
          border: showCl?'1px solid rgba(59,130,246,0.4)':'1px solid rgba(255,255,255,0.08)',
          color: showCl?'#60a5fa':'rgba(200,215,235,0.5)',
        }}>{showCl?'▲ Скрыть статистику':'📊 Статистика ключей'}</button>
      </div>
      {showPos && (
        <div style={{ padding:'0 12px 12px', borderTop:'1px solid rgba(59,130,246,0.15)' }}>
          <PositionsPanel key={posKey} nmId={nmId}/>
        </div>
      )}
      {showCl && (
        <div style={{ padding:'0 12px 12px', borderTop:'1px solid rgba(59,130,246,0.15)' }}>
          <ClustersPanel nmId={nmId} alwaysOpen onAdded={()=>setPosKey(k=>k+1)}/>
        </div>
      )}
      {showChart && (
        <div style={{ padding:'0 12px 12px', borderTop:'1px solid rgba(59,130,246,0.15)' }}>
          <AdvChart nmId={nmId}/>
        </div>
      )}
    </div>
  );
}

export default function Advertising() {
  const _savedAdv = JSON.parse(localStorage.getItem('adv_period') || 'null');
  const currentMode = useRef(_savedAdv?.mode || '7days');
  const _today = todayStr();
  const [from, setFrom] = useState(_savedAdv?.from || toISO(new Date(todayMSK().getTime() - 7*86400000)));
  const [to, setTo] = useState(_savedAdv?.to || _today);
  const [loadedDates, setLoadedDates] = useState(dateRange(_savedAdv?.from || toISO(new Date(todayMSK().getTime() - 7*86400000)), _savedAdv?.to || _today));
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [hidden, setHidden] = useState(new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [tags, setTags] = useState({});
  const [tagFilter, setTagFilter] = useState('all');

  const load = useCallback(async (f, t) => {
    setLoading(true);
    setError('');
    try {
      const [r, hiddenR, tagsR] = await Promise.all([
        api.get(`/advertising?from=${f}&to=${t}`),
        api.get('/monitoring/adv-hidden'),
        api.get('/monitoring/adv-tags'),
      ]);
      const arts = r.data.articles || [];
      setArticles(arts);
      setHidden(new Set((hiddenR.data || []).map(String)));
      setTags(tagsR.data || {});
      setFrom(f);
      setTo(t);
      setLoadedDates(dateRange(f, t));
      localStorage.setItem('adv_period', JSON.stringify({ from: f, to: t, mode: currentMode.current }));
    } catch(e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const mode = _savedAdv?.mode || '7days';
    currentMode.current = mode;
    const today = todayMSK();
    const todayISO = toISO(today);
    let f = from, t = todayISO;
    if (mode === '7days')  f = toISO(new Date(today.getTime() - 7*86400000));
    else if (mode === '14days') f = toISO(new Date(today.getTime() - 14*86400000));
    else if (mode === '30days') f = toISO(new Date(today.getTime() - 30*86400000));
    else if (mode === 'year') { f = `${today.getFullYear()}-01-01`; t = todayISO; }
    else if (mode === 'custom') { f = _savedAdv?.from || from; t = _savedAdv?.to || to; }
    load(f, t);
  }, []); // eslint-disable-line

  const toggleHidden = async (nmId) => {
    const id = String(nmId);
    if (hidden.has(id)) {
      await api.delete(`/monitoring/adv-hidden/${nmId}`);
      setHidden(h => { const n = new Set(h); n.delete(id); return n; });
    } else {
      await api.post(`/monitoring/adv-hidden/${nmId}`);
      setHidden(h => new Set([...h, id]));
    }
  };

  const handleTag = async (nmId) => {
    const id = String(nmId);
    const cur = tags[id] || '';
    const next = cur === '' ? 'top' : cur === 'top' ? 'flop' : '';
    setTags(t => ({ ...t, [id]: next }));
    await api.post(`/monitoring/adv-tags/${nmId}`, { tag: next });
  };

  const dates = loadedDates.length > 0 ? loadedDates : dateRange(from, to);
  const hypoData = useHypothesisData(dates);

  const sortBySpend = (arr) => [...arr].sort((a, b) => {
    const spendA = dates.reduce((s, d) => s + Number(a.days?.[d]?.spend_all || 0), 0);
    const spendB = dates.reduce((s, d) => s + Number(b.days?.[d]?.spend_all || 0), 0);
    return spendB - spendA;
  });
  const filtered = articles.filter(a => {
    const id = String(a.nmId);
    if (showHidden ? !hidden.has(id) : hidden.has(id)) return false;
    if (tagFilter !== 'all' && tags[id] !== tagFilter) return false;
    if (!search) return true;
    return String(a.nmId).includes(search) || a.vendorCode.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Реклама</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Аналитика рекламных кампаний по артикулам</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Поиск по артикулу..."
            style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: 'var(--text)', fontSize: 12, width: 180 }}
          />
          <PeriodSelector onChange={(f, t, m) => { currentMode.current = m || '7days'; load(f, t); }} initialFrom={_savedAdv?.from} initialTo={_savedAdv?.to} initialMode={_savedAdv?.mode} />
          <div style={{ display: 'flex', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 3 }}>
            {[['all','Все'],['top','Топ'],['flop','Слив']].map(([k,l]) => (
              <button key={k} onClick={() => setTagFilter(k)} style={{
                padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', border: 'none',
                background: tagFilter === k ? 'var(--accent)' : 'transparent',
                color: tagFilter === k ? '#fff' : 'var(--text-muted)',
              }}>{l}</button>
            ))}
          </div>
          <button onClick={() => setShowHidden(s => !s)} style={{
            padding: '5px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
            border: '1px solid var(--border)', background: showHidden ? 'rgba(239,68,68,0.15)' : 'var(--surface)',
            color: showHidden ? '#ef4444' : 'var(--text-muted)',
          }}>{showHidden ? '👁 Скрытые' : '👁 Скрытые'}</button>
        </div>
      </div>

      <MassPositions />

      {loading && <CatLoader loading={true} />}
      {error && <div style={{ padding: 32, color: 'var(--red)' }}>Ошибка: {error}</div>}

      {!loading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.length === 0
            ? <div style={{ textAlign: 'center', padding: 64, color: 'var(--text-muted)', fontSize: 15 }}>Нет данных за выбранный период</div>
            : sortBySpend(filtered).map(a => <ArticleCard key={a.nmId} article={a} dates={dates} tag={tags[String(a.nmId)]||''} onHide={toggleHidden} onTag={handleTag} hypoData={hypoData} />)
          }
        </div>
      )}
    </div>
  );
}
